import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import translate from '../../../../commonComponents/translate';
import Form from '../../../../commonComponents/forms/Form';
import FormFooter from '../../../../commonComponents/forms/FormFooter';
import FormSubtitle from '../../../../commonComponents/forms/FormSubtitle';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import ProfileField from './profile/ProfileField';
import Separator from '../../../../commonComponents/forms/Separator';
import { useModal } from '../../../../services/customHooks';
import UnlockUserPageNameModal from '../components/UnlockUserPageNameModal';
import LockIcon from '../../../../commonComponents/icons/LockIcon';
import SkipStepButton from '../../../journey/overall/content/components/SkipStepButton';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import { getValidationRules } from '../../helpers/helper';
import { PROFILE_BLOCK } from '../../../../services/utilityFunctions';

function stateFromProps(member = {}) {
  return {
    userPageName: member.userPageName || '',
    username: member.username || '',
    firstName: member.firstName || '',
    lastName: member.lastName || '',
    email: member.email || '',
    birthday: member.birthday,
    birthName: member.birthName,
    placeOfBirth: member.placeOfBirth,
    maritalStatus: member.maritalStatus || '',
    genre: member.genre || '',
    nationality: member.nationality ? member.nationality.code : '',
  };
}

/**
 * Form to update the current logged user
 */
function InfoForm({
  t,
  onMemberChange,
  member,
  countries,
  previousUserPageName,
  onSkipStep,
}) {
  const { tenant, organization } = useContext(ConfigsContext);
  const { requiredFieldsPerBlock = [] } =
    organization?.services?.me?.config || {};

  // check if there is a userPageName
  const hasUserPageName =
    member.userPageName && member.userPageName?.length > 1;
  const [isLocked, setIsLocked] = useState(hasUserPageName);
  const [showModal, setShowModal] = useModal({ show: false, data: null });

  const [state, setState] = useState(stateFromProps(member));
  useEffect(() => setState(stateFromProps(member)), [member]);

  useEffect(() => {
    if (previousUserPageName?.error === 'ERROR_USER_PAGE_NAME_ALREADY_EXISTS') {
      setIsLocked(false);
      setState(
        stateFromProps({
          ...member,
          userPageName: previousUserPageName?.userPageName || '',
        }),
      );
    }
  }, [previousUserPageName]);

  const formId = 'infoForm';
  const validationRules = useMemo(() => {
    const validation = {
      email: { required: true, email: true },
    };
    return getValidationRules(
      requiredFieldsPerBlock,
      PROFILE_BLOCK.INFO,
      validation,
    );
  }, [getValidationRules, requiredFieldsPerBlock]);

  const handleSubmit = async () => {
    const params = {
      userPageName:
        state?.userPageName && state?.userPageName.length > 0
          ? state?.userPageName
          : null,
      username: state.username,
      firstName: state.firstName,
      lastName: state.lastName,
      email: state.email.toLowerCase(), // needed to be toLowerCase() because UserLogin
      birthday: state.birthday,
      birthName: state.birthName,
      placeOfBirth: state.placeOfBirth,
      maritalStatus: state.maritalStatus,
      genre: state.genre || null,
      nationality: state.nationality
        ? countries.find(c => c.code === state.nationality)
        : null,
    };
    setIsLocked(state?.userPageName && state?.userPageName.length > 0);
    onMemberChange(params);
  };

  const handleChange = useCallback(
    (field, value) => setState(prevState => ({ ...prevState, [field]: value })),
    [],
  );

  const inputProps = useCallback(
    field => {
      switch (field) {
        case 'genre':
          return {
            items: [
              { id: 'male', text: t('gender_male') },
              { id: 'female', text: t('gender_female') },
            ],
            searchable: false,
          };
        case 'nationality':
          return {
            items: countries.map(c => ({
              id: c.code,
              text: `${c.flagEmoji} ${c.name}`,
            })),
          };
        case 'birthday':
          return { startView: 'decade', endDate: Date.now() };
        case 'maritalStatus':
          return {
            items: [
              { id: 'single', text: t('maritalStatus.single') },
              { id: 'married', text: t('maritalStatus.married') },
              { id: 'divorced', text: t('maritalStatus.divorced') },
              { id: 'widower', text: t('maritalStatus.widower') },
              { id: 'marital_life', text: t('maritalStatus.marital_life') },
              { id: 'separated', text: t('maritalStatus.separated') },
              { id: 'pacs', text: t('maritalStatus.pacs') },
            ],
            searchable: false,
          };
        case 'userPageName':
          return {
            pattern: '^[A-Za-z0-9_-]*$',
            disabled: isLocked,
          };
        default:
          return undefined;
      }
    },
    [countries, isLocked],
  );

  const handleUnlock = () => {
    setIsLocked(false);
    setShowModal({ show: false });
  };

  const keepLocked = () => {
    setShowModal({ show: false });
  };

  const handleShowModal = () => {
    setShowModal({ show: true });
  };

  // Validation so its coherent with Share button in SidePanel
  const userPageIsAvailable =
    tenant?.settings?.distributorPageAvailableToAllTypes ||
    member.type === 'collaborator';

  const disableNameChange =
    typeof member?.userCanChangeName === 'boolean'
      ? !member?.userCanChangeName
      : false;
  return (
    <>
      {showModal.show && (
        <UnlockUserPageNameModal onClose={keepLocked} onSave={handleUnlock} />
      )}
      <Form
        className="m-form m-form--fit m-form--label-align-right"
        id={formId}
        submitMethod={handleSubmit}
        validationRules={validationRules}
        customMessages={{
          userPageName: t('profile.info.userPageName.invalidFormat'),
        }}
      >
        {submitHandler => (
          <>
            <div className="m-portlet__body">
              <div className="row profile-skipStep">
                <div className={`${!onSkipStep ? 'col-12' : 'col-6'}`}>
                  <FormSubtitle title={t('profile_tab_info')} />
                </div>
                {onSkipStep && (
                  <div className="col-6 skip-wrapper btn-wrapper">
                    <SkipStepButton onClick={onSkipStep} />
                  </div>
                )}
              </div>
              <ProfileField
                field="username"
                value={state.username}
                onChange={handleChange}
                inputProps={inputProps}
                block={PROFILE_BLOCK.INFO}
                requiredFieldsPerBlock={requiredFieldsPerBlock}
              />
              <ProfileField
                field="firstName"
                value={state.firstName}
                onChange={handleChange}
                inputProps={inputProps}
                block={PROFILE_BLOCK.INFO}
                requiredFieldsPerBlock={requiredFieldsPerBlock}
                disabled={disableNameChange}
              />
              <ProfileField
                field="lastName"
                value={state.lastName}
                onChange={handleChange}
                inputProps={inputProps}
                block={PROFILE_BLOCK.INFO}
                requiredFieldsPerBlock={requiredFieldsPerBlock}
                disabled={disableNameChange}
              />
              <ProfileField
                field="email"
                type="email"
                value={state.email}
                onChange={handleChange}
                inputProps={inputProps}
                disabled={
                  typeof member?.userCanChangeEmail === 'boolean'
                    ? !member?.userCanChangeEmail
                    : false
                }
                block={PROFILE_BLOCK.INFO}
                requiredFieldsPerBlock={requiredFieldsPerBlock}
              />
              <ProfileField
                field="birthday"
                type="date"
                value={state.birthday}
                onChange={handleChange}
                inputProps={inputProps}
                block={PROFILE_BLOCK.INFO}
                requiredFieldsPerBlock={requiredFieldsPerBlock}
              />
              <ProfileField
                field="birthName"
                value={state.birthName}
                onChange={handleChange}
                inputProps={inputProps}
                block={PROFILE_BLOCK.INFO}
                requiredFieldsPerBlock={requiredFieldsPerBlock}
              />
              <ProfileField
                field="placeOfBirth"
                value={state.placeOfBirth}
                onChange={handleChange}
                inputProps={inputProps}
                block={PROFILE_BLOCK.INFO}
                requiredFieldsPerBlock={requiredFieldsPerBlock}
              />
              <ProfileField
                field="genre"
                type="select"
                value={state.genre}
                onChange={handleChange}
                inputProps={inputProps}
                block={PROFILE_BLOCK.INFO}
                requiredFieldsPerBlock={requiredFieldsPerBlock}
              />
              <ProfileField
                field="maritalStatus"
                type="select"
                value={state.maritalStatus}
                onChange={handleChange}
                inputProps={inputProps}
                block={PROFILE_BLOCK.INFO}
                requiredFieldsPerBlock={requiredFieldsPerBlock}
              />
              <ProfileField
                field="nationality"
                type="select"
                value={state.nationality}
                onChange={handleChange}
                inputProps={inputProps}
                block={PROFILE_BLOCK.INFO}
                requiredFieldsPerBlock={requiredFieldsPerBlock}
              />
              {userPageIsAvailable && (
                <>
                  <Separator />
                  <div className="user-page-name-container row">
                    <div className="col-lg-9 col-sm-12 input-wrapper">
                      <ProfileField
                        field="userPageName"
                        containerClassName="col-lg-9 col-sm-12 container"
                        labelClassName="col-lg-3 col-sm-12 label"
                        value={state.userPageName}
                        onChange={handleChange}
                        inputProps={inputProps}
                        groupClassName="group-input"
                        block={PROFILE_BLOCK.INFO}
                        requiredFieldsPerBlock={requiredFieldsPerBlock}
                      />
                    </div>
                    <div className="user-page-lock-wrapper col-lg-1 col-sm-12">
                      {isLocked && (
                        <button
                          type="button"
                          className="user-page-lock"
                          onClick={handleShowModal}
                        >
                          <span className="lock-icon">
                            <LockIcon width={17} height={17} />
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <FormFooter submitHandler={submitHandler} />
          </>
        )}
      </Form>
    </>
  );
}

InfoForm.propTypes = {
  /** translation function */
  t: PropTypes.func.isRequired,
  /** member being edited */
  member: PropTypes.shape(UserProp.propType).isRequired,
  /** member change event handler */
  onMemberChange: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  previousUserPageName: PropTypes.shape({
    error: PropTypes.string,
    userPageName: PropTypes.string,
  }).isRequired,
  onSkipStep: PropTypes.func,
};

InfoForm.defaultProps = {
  onSkipStep: null,
};

export default translate(InfoForm);
